import * as React from 'react';

import { Box, Divider, Stack, Typography } from '@mui/material';

import SvgIcon, { IconName } from '../../Icons';

const SectionTitle: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Typography sx={{ fontSize: '20px', fontWeight: 600, color: '#1A1A1A', lineHeight: 1.3 }}>
    {children}
  </Typography>
);

const BodyText: React.FC<React.PropsWithChildren<{ sx?: object }>> = ({ children, sx }) => (
  <Typography sx={{ fontSize: '14px', color: '#5C5C5C', lineHeight: 1.6, ...sx }}>
    {children}
  </Typography>
);

type ChipProps = { icon: IconName; label: string; iconSize?: string; color: string };

const OptionChip: React.FC<ChipProps> = ({ icon, label, iconSize = '16px', color }) => (
  <Box
    display='flex'
    alignItems='center'
    gap='8px'
    sx={{
      padding: '8px 16px',
      bgcolor: '#FAFAFA',
      border: '1px solid #F0F0F0',
      borderRadius: '24px',
    }}
  >
    <SvgIcon name={icon} sx={{ fontSize: iconSize, color }} />
    <Typography component='span' fontSize='14px' fontWeight={500} color='#1A1A1A'>
      {label}
    </Typography>
  </Box>
);

type OutcomeCardProps = {
  icon: IconName;
  iconSize?: string;
  accent: string;
  title: string;
  description: string;
};

const OutcomeCard: React.FC<OutcomeCardProps> = ({
  icon,
  iconSize = '16px',
  accent,
  title,
  description,
}) => (
  <Box
    sx={{
      borderRadius: '10px',
      padding: '14px 18px',
      bgcolor: '#FAFAFA',
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
    }}
  >
    <SvgIcon name={icon} sx={{ fontSize: iconSize, color: accent }} />
    <Box display='flex' flexDirection='column' gap='2px'>
      <Typography fontSize='15px' fontWeight={600} color='#1A1A1A'>
        {title}
      </Typography>
      <BodyText sx={{ fontSize: '13px' }}>{description}</BodyText>
    </Box>
  </Box>
);

const ACCENT_GREEN = '#34A853';
const ACCENT_RED = '#E5484D';
const ACCENT_NEUTRAL = '#5C5C5C';

const AccessRequestDesc: React.FC = () => (
  <Stack spacing={3} padding={1}>
    <Stack spacing={1.5}>
      <SectionTitle>What is access request?</SectionTitle>
      <BodyText>
        When creating a recurring offer, you can choose how users access your offer by selecting one
        of these options:
      </BodyText>
      <Box display='flex' alignItems='center' gap='8px' flexWrap='wrap'>
        <OptionChip icon='Tick' label='Accept All' color={ACCENT_GREEN} />
        <OptionChip icon='Key' label='Permission Required' color={ACCENT_NEUTRAL} />
        <OptionChip icon='Cross' label='Reject all' iconSize='14px' color={ACCENT_RED} />
      </Box>
    </Stack>

    <Divider />

    <Stack spacing={1.5}>
      <SectionTitle>Permission required</SectionTitle>
      <BodyText>
        If you choose &quot;Permission Required,&quot;{' '}
        <Box component='span' sx={{ fontWeight: 600, color: '#1A1A1A' }}>
          users must request access to reserve your offer.
        </Box>{' '}
        You can then review and either approve or reject each request.
      </BodyText>

      <Stack spacing={1.5} mt={0.5}>
        <OutcomeCard
          icon='Tick'
          accent={ACCENT_GREEN}
          title='Accept'
          description='The user is granted permission and can reserve the offer'
        />
        <OutcomeCard
          icon='Cross'
          iconSize='14px'
          accent={ACCENT_RED}
          title='Reject'
          description='The user will no longer see this offer'
        />
      </Stack>
    </Stack>
  </Stack>
);

export default AccessRequestDesc;
